/**
 * Shared ProseMirror doc <-> plain-text helpers (ARBI-851).
 *
 * One walker replaces the three feature copies, whose node coverage was the
 * union of:
 * - enrollment `override-payload`: `text` + `paragraph` structure (one output
 *   line per paragraph), transparent recursion through any container;
 * - ai-prompts `doc-utils`: `text` + `variable-mention`, flattened to one
 *   space-joined line;
 * - sequences `reply-subject`: `text` + `variable-mention`, concatenated to
 *   one line.
 * The copies deliberately FORMAT differently (multi-line textarea seeding vs
 * single-line previews vs subject lines; `[Label]` vs `{Label}` chips), so the
 * traversal is shared here and each call site picks its format via options.
 */

export type ProseMirrorDoc = {
  type: "doc";
  content: Array<{
    type: "paragraph";
    content?: Array<{ type: "text"; text: string }>;
  }>;
};

type DocNode = {
  type?: string;
  text?: string;
  attrs?: Record<string, unknown> | null;
  content?: unknown[];
};

export type DocToPlainTextOptions = {
  /**
   * Flatten to one line: tokens joined with `separator`, whitespace runs
   * collapsed to single spaces, result trimmed. Default (false) keeps one
   * output line per paragraph, blank paragraphs included.
   */
  singleLine?: boolean;
  /** Token separator in single-line mode. Default `""`. */
  separator?: string;
  /** Render a `variable-mention` node's text; return null to drop the chip.
   *  Omitted: mentions are dropped (they have no children to recurse into). */
  variableMention?: (attrs: Record<string, unknown>) => string | null;
};

/**
 * Wrap plain text into a minimal ProseMirror doc — one paragraph per
 * newline-separated line. Empty lines become empty paragraphs so blank-line
 * spacing survives the round trip through the backend renderer.
 */
export function plainTextToDoc(text: string): ProseMirrorDoc {
  const lines = text.split("\n");
  return {
    type: "doc",
    content: lines.map((line) =>
      line.length === 0
        ? { type: "paragraph" }
        : {
            type: "paragraph",
            content: [{ type: "text", text: line }],
          },
    ),
  };
}

/**
 * Best-effort inverse of {@link plainTextToDoc}: walk a ProseMirror doc and
 * flatten its text leaves. Default output is newline-separated paragraphs;
 * see {@link DocToPlainTextOptions} for the single-line preview formats.
 */
export function docToPlainText(
  doc: unknown,
  options: DocToPlainTextOptions = {},
): string {
  if (!doc) return "";
  if (options.singleLine) {
    const parts: string[] = [];
    collectTokens(doc, options, parts);
    return parts
      .join(options.separator ?? "")
      .replace(/\s+/g, " ")
      .trim();
  }
  return walkParagraphs(doc, options).join("\n");
}

function renderMention(
  node: DocNode,
  options: DocToPlainTextOptions,
): string | null {
  return options.variableMention?.(node.attrs ?? {}) ?? null;
}

// Docs arrive from backend JSON; a malformed `content` must yield an empty
// preview, not a render-time TypeError.
function children(n: DocNode): unknown[] {
  return Array.isArray(n.content) ? n.content : [];
}

function walkParagraphs(
  node: unknown,
  options: DocToPlainTextOptions,
): string[] {
  if (!node || typeof node !== "object") return [];
  const n = node as DocNode;
  if (n.type === "text" && typeof n.text === "string") return [n.text];
  if (n.type === "variable-mention") {
    const chip = renderMention(n, options);
    return chip === null ? [] : [chip];
  }
  if (n.type === "paragraph") {
    const inner = children(n)
      .flatMap((c) => walkParagraphs(c, options))
      .join("");
    return [inner];
  }
  return children(n).flatMap((c) => walkParagraphs(c, options));
}

function collectTokens(
  node: unknown,
  options: DocToPlainTextOptions,
  out: string[],
): void {
  if (!node || typeof node !== "object") return;
  const n = node as DocNode;
  if (n.type === "text" && typeof n.text === "string") {
    out.push(n.text);
    return;
  }
  if (n.type === "variable-mention") {
    const chip = renderMention(n, options);
    if (chip !== null) out.push(chip);
    return;
  }
  for (const child of children(n)) collectTokens(child, options, out);
}
